import { DASHBOARD_MAX_SERIES } from "./record-dashboard-model";

export type StackedBarSegment = {
  key: string;
  label: string;
};

export type StackedBarPoint = {
  label: string;
  total: number;
  yoyDelta: number | null;
} & Record<string, string | number | null>;

export type StackedBarData = {
  points: StackedBarPoint[];
  segments: StackedBarSegment[];
};

export type GroupedBarPoint = {
  label: string;
  total: number;
} & Record<string, string | number | null>;

export type GroupedBarData = {
  points: GroupedBarPoint[];
  segments: StackedBarSegment[];
};

export const DASHBOARD_OTHER_SEGMENT_KEY = "segment_mlx_other";

// The control plane caps segments at 10 but the palette identifies 6; fold the
// value-ranked tail into one "Other" segment instead of repeating hues.
export function foldSegments<
  Point extends Record<string, string | number | null>,
>(
  data: { points: Point[]; segments: StackedBarSegment[] },
  maxSegments = DASHBOARD_MAX_SERIES,
): { points: Point[]; segments: StackedBarSegment[] } {
  if (data.segments.length <= maxSegments) return data;
  const folded = data.segments.slice(maxSegments - 1);
  const foldedCategoryCount = folded.reduce(
    (count, segment) =>
      count +
      (segment.key === DASHBOARD_OTHER_SEGMENT_KEY
        ? categoryCount(segment.label)
        : 1),
    0,
  );
  const segments = [
    ...data.segments.slice(0, maxSegments - 1),
    {
      key: DASHBOARD_OTHER_SEGMENT_KEY,
      label: `Other (${foldedCategoryCount})`,
    },
  ];
  const points = data.points.map((point) => {
    const next: Record<string, string | number | null> = { ...point };
    let otherTotal = 0;
    for (const segment of folded) {
      otherTotal += Number(next[segment.key] ?? 0);
      delete next[segment.key];
    }
    next[DASHBOARD_OTHER_SEGMENT_KEY] = otherTotal;
    return next as Point;
  });
  return { points, segments };
}

function categoryCount(label: string): number {
  const match = /^Other \((\d+)\)$/u.exec(label);
  return match ? Number(match[1]) : 1;
}
